import { LockSimple, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export function PageHeader({ title, description, actions, testId }) {
  return (
    <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-testid={testId}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="admin-page-title">{title}</h1>
        {description && <p className="cw-soft mt-2 max-w-2xl text-sm leading-6">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Panel({ children, className = "", ...rest }) {
  return <div className={`rounded-[20px] border border-border bg-white ${className}`} {...rest}>{children}</div>;
}

export function LoadState({ loading, error, onRetry, children }) {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="admin-loading" aria-busy="true">
        {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-[var(--cw-surface-tertiary)]" />)}
      </div>
    );
  }
  if (error?.status === 403) {
    return (
      <Panel className="flex flex-col items-start p-10" data-testid="admin-forbidden">
        <LockSimple size={28} className="cw-muted" aria-hidden />
        <h2 className="mt-4 text-lg font-semibold">This area isn’t part of your role</h2>
        <p className="cw-soft mt-1 text-sm">{error.message} Ask the Owner if you need access.</p>
      </Panel>
    );
  }
  if (error) {
    return (
      <Panel className="flex flex-col items-start p-10" role="alert" data-testid="admin-error">
        <WarningCircle size={28} className="cw-error" aria-hidden />
        <h2 className="mt-4 text-lg font-semibold">Something didn’t load</h2>
        <p className="cw-soft mt-1 text-sm">{error.message}</p>
        {onRetry && <Button variant="outline" className="mt-5" onClick={onRetry} data-testid="admin-error-retry-button">Try again</Button>}
      </Panel>
    );
  }
  return children;
}

export function Empty({ title, text, testId = "admin-empty" }) {
  return (
    <div className="flex flex-col items-start px-6 py-12" data-testid={testId}>
      <p className="font-medium">{title}</p>
      {text && <p className="cw-soft mt-1 text-sm">{text}</p>}
    </div>
  );
}

const TONES = {
  neutral: "bg-[var(--cw-surface-tertiary)] text-foreground",
  brand: "cw-bg-tint cw-brand-deep",
  success: "cw-tone-success",
  warning: "cw-tone-warning",
  error: "cw-tone-error",
  inverse: "cw-bg-inverse",
};

export function Pill({ tone = "neutral", children, className = "", ...rest }) {
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONES[tone]} ${className}`} {...rest}>{children}</span>;
}
